import Config from './config';

/**
 * Gets the subdirectory prefix (e.g., '/TBFE-1955-2' or '').
 * Works both server-side (from config) and client-side (from window variable).
 * @returns The subdirectory prefix (e.g., '/TBFE-1955-2' or '').
 */
export const getSubdirectory = (): string => {
  // Try Config.subdirectory first (works server-side and client-side after config loads).
  if (Config.subdirectory) {
    return Config.subdirectory;
  }

  // Fall back to window variable (client-side, set in index.html).
  if (typeof window !== 'undefined' && (window as any).__TB_SUBDIRECTORY__) {
    return (window as any).__TB_SUBDIRECTORY__;
  }

  return '';
};

/**
 * Gets a static asset path (images, fonts, CSS, JS, etc.) with subdirectory prefix.
 * Handles absolute URLs (http://, https://) by returning them as-is.
 * @param path - The asset path (e.g., '/images/logo.png' or 'images/logo.png').
 * @returns The path with subdirectory prefix if configured.
 */
export const getStaticPath = (path: string): string => {
  // If path is already an absolute URL, return as-is.
  if (path.startsWith('http://') || path.startsWith('https://')) {
    return path;
  }

  // Ensure path starts with /.
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;

  let subdirectory = getSubdirectory();

  // Remove trailing slash from subdirectory if present.
  const cleanSubdirectory = subdirectory.endsWith('/') ? subdirectory.slice(0, -1) : subdirectory;

  return cleanSubdirectory ? `${cleanSubdirectory}${normalizedPath}` : normalizedPath;
};

/**
 * Gets a full URL with subdirectory prefix for use with window.location.href.
 * This is specifically for redirects that bypass React Router.
 * @param path - The path (e.g., '/no-account?grp=tb' or '/status').
 * @returns The full path with subdirectory prefix if configured.
 */
export const getFullUrl = (path: string): string => {
  return getStaticPath(path);
};
